import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  PDF_BRAND_FOOTER_MARGIN,
  loadPdfBrandAssets,
  pdfBrandDidDrawPage,
} from "./pdfBrand";

const PRESENT_RGB = [34, 140, 78];
const ABSENT_RGB = [196, 48, 48];
const MUTED_RGB = [140, 135, 155];
const TITLE_RGB = [48, 45, 69];
const SUB_RGB = [81, 75, 101];

function statusLabel(status) {
  if (status === "present") return "P";
  if (status === "absent") return "A";
  return "—";
}

function safeFilenamePart(value, max = 40) {
  return String(value || "course")
    .replace(/[^\w-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, max);
}

/**
 * Build and download a landscape monthly attendance matrix PDF.
 * Columns: Name + one column per recorded class date in the month.
 */
export async function downloadMonthlyAttendancePdf({
  courseTitle,
  periodLabel,
  year,
  month,
  dates,
  students,
}) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const marginX = 12;
  const assets = await loadPdfBrandAssets(doc);

  const dayHeaders = dates.map((iso) => {
    const day = String(iso).slice(8, 10);
    return day.replace(/^0/, "") || day;
  });

  const head = [["Name", ...dayHeaders]];
  const body = students.map((student) => [
    student.name || "—",
    ...(student.statuses || []).map(statusLabel),
  ]);

  let cursorY = assets.headerH + 8;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...TITLE_RGB);
  doc.text("Monthly attendance report", marginX, cursorY);
  cursorY += 6;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...SUB_RGB);
  doc.text(`${courseTitle || "Course"} · ${periodLabel || `${year}-${month}`}`, marginX, cursorY);
  cursorY += 5;

  doc.setFontSize(9);
  doc.text(
    `Classes recorded: ${dates.length}  ·  Students: ${students.length}  ·  Legend: P = Present, A = Absent, — = Not on that day's roster`,
    marginX,
    cursorY,
  );
  cursorY += 6;

  const dayCount = Math.max(dates.length, 1);
  const pageWidth = doc.internal.pageSize.getWidth();
  const usableWidth = pageWidth - marginX * 2;
  const nameWidth = Math.min(52, usableWidth * 0.22);
  const dayWidth = Math.max(7, (usableWidth - nameWidth) / dayCount);

  doc.autoTable({
    startY: cursorY,
    head,
    body,
    margin: {
      top: assets.headerH + 6,
      left: marginX,
      right: marginX,
      bottom: PDF_BRAND_FOOTER_MARGIN,
    },
    didDrawPage: pdfBrandDidDrawPage(
      doc,
      assets,
      "monthly attendance report",
      marginX,
    ),
    horizontalPageBreak: true,
    horizontalPageBreakRepeat: 0,
    styles: {
      font: "helvetica",
      fontSize: dayCount > 16 ? 8 : 9,
      cellPadding: dayCount > 20 ? 1.4 : 2,
      halign: "center",
      valign: "middle",
      textColor: TITLE_RGB,
      overflow: "linebreak",
      minCellHeight: 6,
    },
    headStyles: {
      fillColor: [213, 204, 253],
      textColor: [64, 59, 85],
      fontStyle: "bold",
      halign: "center",
      fontSize: dayCount > 16 ? 7.5 : 8.5,
    },
    columnStyles: {
      0: {
        cellWidth: nameWidth,
        halign: "left",
        fontStyle: "bold",
        overflow: "ellipsize",
      },
      ...Object.fromEntries(
        dates.map((_, i) => [
          i + 1,
          {
            cellWidth: dayWidth,
            halign: "center",
            fontStyle: "bold",
          },
        ]),
      ),
    },
    didParseCell: (data) => {
      if (data.section !== "body" || data.column.index === 0) return;
      const value = String(data.cell.raw || "");
      if (value === "P") {
        data.cell.styles.textColor = PRESENT_RGB;
      } else if (value === "A") {
        data.cell.styles.textColor = ABSENT_RGB;
      } else {
        data.cell.styles.textColor = MUTED_RGB;
        data.cell.styles.fontStyle = "normal";
      }
    },
  });

  const safeCourse = safeFilenamePart(courseTitle);
  const ym = `${year}-${String(month).padStart(2, "0")}`;
  doc.save(`attendance_monthly_${safeCourse}_${ym}.pdf`);
}
